<script setup lang="ts">
import { storeToRefs } from "pinia";
import TagDropdown from "~/components/Common/Dropdowns/TagDropdown.vue";
import SwitchInput from "~/components/Common/Inputs/SwitchInput.vue";
import Tooltip from "~/components/Common/Tooltip.vue";
import DisabledTooltipWrapper from "~/components/Common/DisabledTooltipWrapper.vue";
import SimpleDropdown from "~/components/Common/Dropdowns/SimpleDropdown.vue";
import { useCategoriesStore } from "~/store/categories/categories.store";
import { useSkillsStore } from "~/store/skills/categories.store";
import { useTrainingPartnersStore } from "~/store/training-partners/training-partners.store";
import { useCreateTrainingStore } from "~/store/trainings/create-training.store";
import { useUsersStore } from "~/store/users/users.store";
import { SessionItemFormatEnum, SessionItemTypeEnum } from "~/utils/enums/session-items.enums";
import { TrainingTypeEnum, VisibleStatus } from "~/utils/enums/training.enums";
import type { TrainingPartnerListItem } from "@simbelapp/order-sdk";
import type { UserCompanyModel } from "@simbelapp/order-sdk";
import CommonInput from "~/components/Common/Inputs/CommonInput.vue";
import GroupedDropdown from "~/components/Common/Dropdowns/GroupedDropdown.vue";
import { useCompanyTagsStore } from "~/store/companies/tags.store";
import { useAuthStore } from "~/store/auth/auth.store";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import VisibilitySelector from "~/components/Common/Visibility/VisibilitySelector.vue";
import { useCompanyUsersDropdown } from "~/composables/users/useCompanyUsersDropdown";
import RadioInput from "~/components/Common/Inputs/RadioInput.vue";
import AiFieldMarker from "~/components/Pages/company-catalog/training-modal/prefill/AiFieldMarker.vue";
import AiFilledField from "~/components/Pages/company-catalog/training-modal/prefill/AiFilledField.vue";
import AiPrefillNotice from "~/components/Pages/company-catalog/training-modal/prefill/AiPrefillNotice.vue";
import { useTrainingPrefillStore } from "~/store/copilot/training-prefill.store";
import { TrainingPrefillFieldEnum } from "~/utils/enums/training-prefill.enums";

const { t } = useI18n();
const createTraining = useCreateTrainingStore();
const prefillStore = useTrainingPrefillStore();
const { training, readonly } = storeToRefs(createTraining);

const isExternalUrlLocked = computed(() => !!training.value.general.lmsId && !training.value.general.isSimbelMaster);
const lmsUrlLockMessage = "Ce lien est géré par votre LMS et ne peut pas être modifié depuis Simbel.";
const tagsStore = useCompanyTagsStore();
const { trainingTags } = storeToRefs(tagsStore);
const categoryStore = useCategoriesStore();
const skillsStore = useSkillsStore();
const trainingPartnersStore = useTrainingPartnersStore();
const usersStore = useUsersStore();
const partnerName = ref("");
const authStore = useAuthStore();
const { user } = storeToRefs(authStore);

function newPartner() {
  createTraining.selectNewTrainingPartner(partnerName.value);
}

const trainingPartner = computed({
  get() {
    return training.value.general.trainingPartner ?? null;
  },
  set(newValue: any) {
    if (newValue) {
      training.value.general.trainingPartner = newValue;
      training.value.general.mainContactEmail = newValue.mainContactEmail;
      training.value.disabled.mainContactEmail = !!newValue.mainContactEmail;
    } else {
      training.value.general.trainingPartner = null;
      training.value.general.mainContactEmail = null;
      training.value.disabled.mainContactEmail = false;
    }
  },
});

onMounted(async () => {
  await categoryStore.fetchCategories();
  await skillsStore.fetchSkills();
  await trainingPartnersStore.fetchTrainingPartnersLazyWithStore();
  if (user.value?.mainEntityId) {
    tagsStore.getTrainingTags({ companyId: user.value.mainEntityId.companyId });
    tagsStore.getSessionTags(user.value.mainEntityId.companyId);
  }
});

watch(
  () => training.value.general.type,
  (newValue) => {
    if (newValue === TrainingTypeEnum.INTERNAL) {
      training.value.modalities.isNotBillable = true;
    } else training.value.modalities.isNotBillable = false;

    training.value.errors = {};

    training.value.sessionItems.group = createTraining.resetSessionItem(SessionItemTypeEnum.GROUP);
    training.value.sessionItems.individual = createTraining.resetSessionItem(SessionItemTypeEnum.INDIVIDUAL);

    if (!training.value.general.lmsId) {
      training.value.general.externalUrl = null;
      training.value.general.internalUrl = null;
    }

    if (!training.value.general.trainingPartner?.mainContactEmail) {
      training.value.general.mainContactEmail = null;
    }

    training.value.general.trainingPartner = null;
    training.value.general.durationDays = null;
    training.value.general.durationHours = null;
    training.value.general.visibility = {
      visible: VisibleStatus.HIDDEN,
      groups: [],
    };
    training.value.general.categories = null;
    training.value.general.skills = null;
    training.value.general.title = null;
    training.value.general.format = null;
  },
);

const servicesAPITrainingPartners = (params: Record<string, any>) =>
  trainingPartnersStore.searchTrainingPartners(params);

const groupedOptions = computed(() => {
  const familyMap = new Map<string, { tagFamilyId: string; tagFamilyName: string; options: any[] }>();

  trainingTags.value.forEach((tag) => {
    if (!tag?.tagFamilyId) return;
    if (!familyMap.has(tag.tagFamilyId)) {
      if (tag.tagFamilyName) {
        familyMap.set(tag.tagFamilyId, {
          tagFamilyId: tag.tagFamilyId,
          tagFamilyName: tag.tagFamilyName,
          options: [],
        });
      }
    }

    familyMap.get(tag.tagFamilyId)?.options.push(tag);
  });
  return Array.from(familyMap.values());
});

async function getTrainingTags() {
  if (user.value?.mainEntityId) {
    await tagsStore.getTrainingTags({ companyId: user.value.mainEntityId.companyId, nullable: true });
  }
}

const { companyUsersServicesAPI } = useCompanyUsersDropdown();
</script>

<template>
  <h3 class="heading_3_demi">Informations générales</h3>
  <div class="training__step_container">
    <AiPrefillNotice
      v-if="prefillStore.isActive"
      class="mb-6"
      :mode="prefillStore.mode"
      :has-document="prefillStore.hasDocument"
      :viewer-open="prefillStore.isViewerOpen"
      @open-viewer="prefillStore.openViewer()"
    />
    <div class="fields">
      <div>
        <label class="text-secondary-color !flex flex-row items-center gap-2">
          Prestataire*
          <AiFieldMarker v-if="prefillStore.isPrefilled(TrainingPrefillFieldEnum.TRAINING_PARTNER)" />
        </label>
        <AiFilledField :active="prefillStore.isPrefilled(TrainingPrefillFieldEnum.TRAINING_PARTNER)">
          <SimpleDropdown
            v-model="trainingPartner"
            :options="(trainingPartnersStore.trainingPartners as TrainingPartnerListItem[]) || []"
            :add="!!partnerName.length"
            add-on-tab
            placeholder="Rechercher par nom"
            searchable
            label="name"
            :error="!!training.errors['general.trainingPartner']"
            :loading="!trainingPartnersStore.trainingPartners"
            :disabled="readonly"
            :store="trainingPartnersStore"
            store-object="trainingPartners"
            :services-a-p-i="servicesAPITrainingPartners"
            :limiter="3"
            @search-value="(x) => (partnerName = x)"
            @click-add="newPartner()"
          >
            <template #singleLabel="{ option }"
              ><span>{{ option.name }}</span></template
            >
            <template #option="{ option }"
              ><span>{{ option.name }}</span></template
            >
            <template #add> Ajouter `{{ partnerName }}` </template>
          </SimpleDropdown>
        </AiFilledField>
        <span v-if="!!training.errors['general.trainingPartner']" class="text-primary-red-color">
          {{ training.errors["general.trainingPartner"] }}
        </span>
      </div>
      <div>
        <label class="text-secondary-color !flex flex-row items-center gap-2">
          Nom de la formation*
          <AiFieldMarker v-if="prefillStore.isPrefilled(TrainingPrefillFieldEnum.TITLE)" />
        </label>
        <AiFilledField :active="prefillStore.isPrefilled(TrainingPrefillFieldEnum.TITLE)">
          <CommonInput
            v-model="training.general.title"
            style="text-align: start"
            placeholder="Saisir le nom de la formation"
            :error="!!training.errors['general.title']"
            :disabled="readonly"
          />
        </AiFilledField>
        <span v-if="!!training.errors['general.title']" class="text-primary-red-color">
          {{ training.errors["general.title"] }}
        </span>
      </div>
      <div>
        <label class="block text-secondary-color !flex flex-row items-center gap-2">
          Format*
          <AiFieldMarker v-if="prefillStore.isPrefilled(TrainingPrefillFieldEnum.FORMAT)" />
        </label>
        <AiFilledField :active="prefillStore.isPrefilled(TrainingPrefillFieldEnum.FORMAT)">
          <div class="flex gap-10">
            <label
              v-for="item in SessionItemFormatEnum"
              :key="item"
              class="!flex w-fit items-center gap-2 !mb-0"
              :class="readonly ? 'cursor-not-allowed' : 'cursor-pointer'"
            >
              <RadioInput
                v-model="training.general.format"
                :value="item"
                :disabled="readonly"
                name="training-general-format"
              />
              <span class="text-main-color">{{ t(item) }}</span>
            </label>
          </div>
        </AiFilledField>
        <span v-if="!!training.errors['general.format']" class="text-primary-red-color">
          {{ training.errors["general.format"] }}
        </span>
      </div>
    </div>
    <div class="flex flex-row gap-4 mb-4">
      <div v-if="training.general.type === TrainingTypeEnum.EXTERNAL" class="flex-1">
        <div class="flex flex-col">
          <div class="inline-flex items-center">
            <label class="text-secondary-color mr-1 !flex flex-row items-center gap-2">
              Contact prestataire
              <Tooltip :position="`top-right`">
                <template #content>
                  <div class="flex w-[342px] gap-[8px]">
                    <img src="/icons/emojis/eyes.svg" alt="" :style="{ height: '16px', width: '16px' }" />
                    Le contact prestataire est notifié à la confirmation d’une inscription et à la fin d’une formation
                    pour transmettre les documents nécessaires.
                  </div>
                </template>
              </Tooltip>
            </label>
          </div>
          <CommonInput
            v-model="training.general.mainContactEmail"
            style="text-align: start"
            placeholder="prenom@mail.com"
            :error="!!training.errors['general.mainContactEmail']"
            :disabled="readonly"
          />
          <span v-if="!!training.errors['general.mainContactEmail']" class="text-primary-red-color">
            {{ training.errors["general.mainContactEmail"] }}
          </span>
        </div>
      </div>
      <div :class="training.general.type === TrainingTypeEnum.EXTERNAL ? 'flex-1' : 'w-full'">
        <div class="inline-flex items-center w-full">
          <label
            class="text-secondary-color mr-1 !flex flex-row items-center gap-2"
            :class="training.general.type === TrainingTypeEnum.EXTERNAL ? 'justify-center' : 'justify-between w-full'"
          >
            Référent(s) en interne
            <Tooltip :position="`top-left`">
              <template #content>
                <div class="flex w-[342px] gap-[8px]">
                  <img src="/icons/emojis/eyes.svg" alt="" :style="{ height: '16px', width: '16px' }" />
                  Le référent peut éditer les informations de cette formation, organiser des sessions et désigner
                  leur(s) référent(s) - qui pourront inviter des participants, déclarer la session terminée...
                </div>
              </template>
            </Tooltip>
          </label>
        </div>
        <TagDropdown
          v-model="training.general.trainingAdmins"
          :disabled="readonly"
          :loading="!usersStore.companyUsers"
          :options="usersStore.companyUsers!"
          placeholder="Choisissez un ou plusieurs référent(s)"
          label="searchFields"
          track-by="displayValue"
          :store="usersStore"
          store-object="companyUsers"
          :services-a-p-i="companyUsersServicesAPI"
        >
          <template #tag="{ option }">
            <span v-if="option.displayValue">{{ option.displayValue }}</span>
            <span v-else-if="option.email">{{ option.email }}</span>
            <span v-else>{{ option.employeeNumber }}</span>
          </template>
          <template #option="{ option }">
            <span>{{ option.displayValue }} </span>
          </template>
        </TagDropdown>
      </div>
    </div>
    <div v-if="training.general.lmsId" class="flex items-center gap-2 mb-3">
      <SwitchInput
        v-model="training.general.isSimbelMaster"
        :disabled="readonly"
        :label="training.general.isSimbelMaster ? 'Géré depuis Simbel' : 'Géré depuis votre LMS'"
      />
      <Tooltip :position="`top-right`">
        <template #content>
          <div class="flex w-[342px] gap-[8px]">
            <img src="/icons/emojis/eyes.svg" alt="" :style="{ height: '16px', width: '16px' }" />
            Si une formation est gérée depuis votre LMS, nous y récupérons les données nom de formation, durée,
            catégories, langue, status, description. Les autres données peuvent être éditées dans Simbel. Les sessions
            liées sont gérées directement depuis le LMS
          </div>
        </template>
      </Tooltip>
    </div>
    <div class="flex flex-col mb-4">
      <label class="text-secondary-color !flex flex-row items-center gap-2">
        Lien vers la formation
        <AiFieldMarker v-if="prefillStore.isPrefilled(TrainingPrefillFieldEnum.EXTERNAL_URL)" />
      </label>
      <AiFilledField :active="prefillStore.isPrefilled(TrainingPrefillFieldEnum.EXTERNAL_URL)">
        <DisabledTooltipWrapper
          :disabled="isExternalUrlLocked"
          tooltip-position="top-mid"
          :tooltip-message="lmsUrlLockMessage"
        >
          <template #button>
            <CommonInput
              v-model="training.general.externalUrl"
              style="text-align: start"
              placeholder="https://site-du-prestataire.com"
              :error="!!training.errors['general.externalUrl']"
              :disabled="readonly || isExternalUrlLocked"
            />
          </template>
        </DisabledTooltipWrapper>
      </AiFilledField>
      <span v-if="!!training.errors['general.externalUrl']" class="text-primary-red-color">
        {{ training.errors["general.externalUrl"] }}
      </span>
    </div>
    <div class="flex flex-col mb-4">
      <label class="text-secondary-color">Lien d’accès à la formation</label>
      <DisabledTooltipWrapper
        :disabled="!!training.general.lmsId"
        tooltip-position="top-mid"
        :tooltip-message="lmsUrlLockMessage"
      >
        <template #button>
          <CommonInput
            v-model="training.general.internalUrl"
            type="url"
            style="text-align: start"
            placeholder="https://site-du-prestataire.com"
            :error="!!training.errors['general.internalUrl']"
            :disabled="readonly || !!training.general.lmsId"
          />
        </template>
      </DisabledTooltipWrapper>
      <span v-if="!!training.errors['general.internalUrl']" class="text-primary-red-color">
        {{ training.errors["general.internalUrl"] }}
      </span>
    </div>
    <div class="flex flex-row mb-6 gap-4">
      <div class="flex-1">
        <label class="text-secondary-color !flex flex-row items-center gap-2">
          Durée de la formation*
          <Tooltip :position="`top-right`">
            <template #content>
              <div class="flex w-[342px] gap-[8px]">
                <img src="/icons/emojis/eyes.svg" alt="" :style="{ height: '16px', width: '16px' }" />
                Si la formation dure 4h30 renseignez 4.5 heures
              </div>
            </template>
          </Tooltip>
          <AiFieldMarker v-if="prefillStore.isPrefilled(TrainingPrefillFieldEnum.DURATION_HOURS)" />
        </label>
        <div class="flex flex-row items-center gap-2">
          <AiFilledField :active="prefillStore.isPrefilled(TrainingPrefillFieldEnum.DURATION_HOURS)">
            <span>
              <CommonInput
                v-model="training.general.durationHours"
                :nullable="true"
                :disabled="readonly"
                style="width: 144px"
                placeholder="-"
                type="number"
                :min="0"
                :error="!!training.errors['general.durationHours']"
              ></CommonInput>
            </span>
          </AiFilledField>
          <span class="text-tertiary-color">heure(s)</span>
        </div>
        <span v-if="!!training.errors['general.durationHours']" class="text-primary-red-color">
          {{ training.errors["general.durationHours"] }}
        </span>
      </div>
      <div class="flex-1">
        <label class="text-secondary-color !flex flex-row items-center gap-2">
          Réparties sur*
          <AiFieldMarker v-if="prefillStore.isPrefilled(TrainingPrefillFieldEnum.DURATION_DAYS)" />
        </label>
        <div class="flex flex-row items-center gap-2">
          <AiFilledField :active="prefillStore.isPrefilled(TrainingPrefillFieldEnum.DURATION_DAYS)">
            <span>
              <CommonInput
                v-model="training.general.durationDays"
                :nullable="true"
                :disabled="readonly"
                style="width: 144px"
                placeholder="-"
                type="number"
                :min="0"
                :error="!!training.errors['general.durationDays']"
              ></CommonInput>
            </span>
          </AiFilledField>
          <span class="text-tertiary-color">jour(s)</span>
        </div>
        <span v-if="!!training.errors['general.durationDays']" class="text-primary-red-color">
          {{ training.errors["general.durationDays"] }}
        </span>
      </div>
    </div>
    <div class="flex flex-col gap-4">
      <div class="flex gap-1 items-center">
        <span class="body_demi">Catégorisation et règles de visibilité</span>
        <Tooltip :position="`top-right`">
          <template #content>
            <div class="flex w-[342px] gap-[8px]">
              <img src="/icons/emojis/eyes.svg" alt="" :style="{ height: '16px', width: '16px' }" />
              <span>En rendant visible la formation au catalogue, elle sera disponible pour vos équipes. </span>
            </div>
          </template>
        </Tooltip>
      </div>
      <div class="flex flex-row gap-4">
        <div class="flex flex-col gap-4 flex-1">
          <div>
            <label class="text-secondary-color">Catégorie*</label>
            <SimpleDropdown
              v-model="training.general.categories"
              :disabled="readonly"
              :options="categoryStore.categories || []"
              placeholder="Choisir une option"
              :error="!!training.errors['general.categories']"
              :loading="!categoryStore.categories"
              :searchable="true"
              label="name"
              track-by="categoryId"
              :delete="!readonly"
            >
              <template #singleLabel="{ option }">
                <span class="truncate">{{ option.name }}</span>
              </template>
              <template #option="{ option }">
                <span class="truncate">{{ option?.name }}</span>
              </template>
            </SimpleDropdown>
            <span v-if="!!training.errors['general.categories']" class="text-primary-red-color">
              {{ training.errors["general.categories"] }}
            </span>
          </div>
        </div>
        <div class="flex flex-col gap-4 flex-1">
          <div>
            <div v-if="authStore.isCompanyAdmin" class="flex flex-row items-center mb-1 gap-2 justify-between">
              <p class="body_regular text-secondary-color">Tag formation</p>
              <div
                class="cursor-pointer button_secondary_default"
                @click="navigateTo('settings?section=training-tags', { open: { target: '_blank' } })"
              >
                <span class="body_regular">Paramétrer</span>
                <SvgIcon name="link" class="w-[14px] h-[14px]"></SvgIcon>
              </div>
            </div>

            <div v-else class="flex flex-row items-center mb-1 gap-1">
              <p class="body_regular text-secondary-color">Tag formation</p>
              <Tooltip :position="'top-right'" class="ml-1">
                <template #content>
                  <div class="flex w-[274px] gap-[8px]">
                    <img src="/icons/emojis/eyes.svg" alt="" :style="{ height: '16px', width: '16px' }" />
                    <span class="font-medium text_kpi"
                      >Contactez votre Administrateur pour créer, modifier ou supprimer un tag.</span
                    >
                  </div>
                </template>
              </Tooltip>
            </div>
            <GroupedDropdown
              v-model="training.general.trainingTags"
              group-values="options"
              group-label="tagFamilyName"
              searchable
              :options="groupedOptions"
              remove
              placeholder="Sélectionner un tag"
              track-by="name"
              label="name"
              color="tagFamilyColor"
              :disabled="readonly"
              open-fetch
              deleted-key="deletedAt"
              @open="getTrainingTags"
            >
            </GroupedDropdown>
          </div>
        </div>
      </div>

      <VisibilitySelector
        v-model="training.general.visibility"
        :readonly="readonly"
        :validation-errors="!!training.errors['general.visibility.groups']"
      />

      <div class="mt-16"></div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.training__step_container {
  @apply mt-4 px-4;

  label {
    @apply block mb-[4px];
  }
}

.fields {
  @apply flex flex-col mb-8 gap-4;
}
</style>
